<template>
    <component
        :is="button.is"
        :class="classes"
        class="th-button"
        v-bind="button.bind"
    >
        <Icon
            v-if="iconLeading"
            :icon="iconLeading"
            class="icon-leading"
        />

        <slot name="default"></slot>

        <Icon
            v-if="iconTrailing"
            :icon="iconTrailing"
            class="icon-trailing"
        />
    </component>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue'
import { assertNever } from '@/helpers'
import {
    type ButtonProps,
    useThButton,
} from '@/components/ui/buttons/useThButton'
import { useSlotText } from '@/components/ui/buttons/useSlotText'
import type { Colors } from '@/components/ui'
import type {
    ButtonColors,
    ButtonSize,
    ButtonTextSize,
    ButtonType,
} from '@/components/ui/buttons/index'
import { getColorForBackground } from '@/components/ui/buttons/index'
import type { Icons } from '@/components/ui/icon'

import Icon from '@/components/ui/icon/Icon.vue'

const props = withDefaults(
    defineProps<
        ButtonProps & {
            type?: ButtonType
            color?: ButtonColors
            textSize?: ButtonTextSize
            fixedWidth?: boolean
            fullWidth?: boolean
            size?: ButtonSize
            iconLeading?: Icons
            iconTrailing?: Icons
        }
    >(),
    {
        type: 'solid',
        color: 'var(--blue)',
        textSize: 14,
        fixedWidth: false,
        fullWidth: false,
        size: 'medium',
        iconLeading: undefined,
        iconTrailing: undefined,
        buttonElementType: undefined,
    }
)

const emit = defineEmits<{
    click: [v: PointerEvent]
}>()

const slots = useSlots()
const defaultSlotContent = useSlotText(slots['default'])

const button = useThButton(props, emit, defaultSlotContent)

const classes = computed(() => ({
    [props.type]: true,
    disabled: props.disabled,
    loading: props.loading && !props.disabled,
    'fixed-width': props.fixedWidth,
    'full-width': props.fullWidth,
    [props.size.toLocaleLowerCase()]: true,
    [`size-${props.textSize}`]: props.type === 'link',
}))

const background = computed((): Colors => {
    const type = props.type

    switch (type) {
        case 'solid':
        // fallthrough
        case 'bordered':
            return props.color
        case 'link':
            return 'var(--green)'
        default:
            return assertNever(type, 'var(--slate)')
    }
})

const linkUnderline = computed((): Colors => {
    const color = props.color

    switch (color) {
        case 'var(--blue)':
        // fallthrough
        case 'var(--slate)':
            return 'var(--green)'
        default:
            return props.color
    }
})

const colorForFilledBackground = computed(
    (): Colors => getColorForBackground(props.color)
)

const colorVar = computed((): Colors => {
    const type = props.type

    switch (type) {
        case 'solid':
            return colorForFilledBackground.value
        case 'bordered':
            return background.value
        case 'link':
            return props.color
        default:
            return assertNever(type, 'var(--slate)')
    }
})

const outlinedHoverColorVar = colorForFilledBackground
</script>

<style lang="scss" scoped>
.th-button {
    font-family: $effra-font-stack;
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 1.313rem;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    //display: flex;
    display: inline-block;
    //align-items: center;
    //justify-content: center;
    cursor: pointer;

    &.solid,
    &.bordered {
        padding: 12px 30px;
        //height: 50px;
        border-radius: 50px;
    }

    &.solid {
        border: 1px solid v-bind(background);
        /* stylelint-disable */
        background-color: v-bind(background);
        /* stylelint-enable */
        color: v-bind(colorVar);
    }

    &.bordered {
        background-color: transparent;
        border: 1px solid v-bind(background);
        color: v-bind(colorVar);

        &:hover {
            /* stylelint-disable */
            background-color: v-bind(background);
            /* stylelint-enable */
            border: 1px solid v-bind(background); // todo hover state border?
            color: v-bind(outlinedHoverColorVar);
        }
    }

    &.small {
        font-size: 0.75rem;
        line-height: 1.25rem;
        padding: 7px 30px;
    }

    &.fixed-width {
        text-align: center;
        width: 100%;
        max-width: 230px;

        &.small {
            max-width: 160px;
        }
    }

    &.full-width {
        text-align: center;
        width: 100%;
    }

    @mixin disabled {
        background-color: $white;
        color: $grey-300;
        border-color: $white;
    }

    &.disabled {
        @include disabled;

        cursor: not-allowed;

        &:hover {
            @include disabled;
        }
    }

    @mixin loading {
        color: $white;
        border-color: transparent;
        background: rgb(0 0 0 / 28%);
        box-shadow: 0 4px 30px rgb(0 0 0 / 10%);
        backdrop-filter: blur(5.2px);

        position: relative;
        overflow: hidden;

        --root-color: rgb(256 256 256 / 28%);
        --animation-duration: 2s;
        --animation-blur-width: 200px;
        --animation-start: -500px;
        --animation-end: 500px;

        &.fixed-width {
            --animation-blur-width: 400px;
            --animation-start: -600px 0;
            --animation-end: 600px 0;
        }

        &::after {
            content: '';
            position: absolute;
            width: 150%;
            height: 150%;
            top: -20%;
            left: -20%;
            display: block;
            overflow: hidden;
            background-image: linear-gradient(
                90deg,
                rgb(0 0 0 / 0%) 0,
                var(--root-color) 50%,
                rgb(0 0 0 / 0%) 100%
            );
            background-size: var(--animation-blur-width) 100%;
            background-position: var(--animation-start);
            background-repeat: no-repeat;
            animation: loading var(--animation-duration) infinite;
        }

        @keyframes loading {
            to {
                background-position: var(--animation-end);
            }
        }
    }

    &.loading {
        cursor: progress;
        @include loading;

        &:hover {
            @include loading;
        }
    }

    // TODO: Review styles below
    &.link {
        width: unset;
        min-height: unset;
        min-width: unset;
        padding: 0;
        border-radius: 0;
        background-color: transparent;
        border: none;
        font-weight: 400;
        color: v-bind(color);
        border-bottom: 1px solid v-bind(linkUnderline);

        &.size-12 {
            font-size: 0.75rem;
        }

        &.size-14 {
            padding: 0 0 2px;
            font-size: 0.875rem;
        }

        &.size-16 {
            padding: 0 0 4px;
            font-size: 1rem;
        }

        &.size-19 {
            padding: 0 0 6px;
            font-size: 1.25rem;
        }
    }

    .icon {
        &.icon-leading {
            margin-right: 7px;
        }

        &.icon-trailing {
            margin-left: 7px;
        }
    }
}
</style>
